import { useState } from 'react';
import { AlertBanner, Badge, Button, Dialog, EmptyState, Input, Select, Toast } from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints, type LocationRow } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';

/**
 * LOKASI & AREA — Figma `40:2` · M01 · rute `/locations` · izin MGR.
 *
 * TIGA AKSI PETA §5.7, DAN SATU YANG SENGAJA TIDAK ADA
 * ---------------------------------------------------
 * Peta menuntut: tambah/ubah area · cetak label QR · nonaktifkan area (data lama tetap
 * terbaca) · pindahkan area ke unit lain. Yang dibangun di sini adalah tambah, nonaktifkan,
 * dan pindah unit. Cetak label QR TIDAK ada, dan alasannya bukan kelalaian:
 * `GET/POST /v1/locations/{id}/qr-tags` belum punya handler, jadi sebuah tombol "Cetak QR"
 * akan memanggil rute yang menjawab `501`. Kondisi "QR belum dicetak" TETAP ditampilkan —
 * ia keadaan yang perlu diketahui manajer — dan ia mengatakan apa adanya.
 *
 * **MENONAKTIFKAN BUKAN MENGHAPUS, dan layar ini menyatakannya di dialognya.** Peta menulis
 * "data lama tetap terbaca" sebagai janji; dialog yang berbunyi "Hapus area?" akan membuat
 * manajer percaya sebaliknya dan tidak berani menekannya. Yang ditulis adalah apa yang
 * benar-benar terjadi: areanya berhenti menerima temuan baru, dan temuan lamanya tetap ada.
 */
export function Locations() {
  const { t, translator } = useSession();
  const data = useResource(
    async () => {
      const [locations, units] = await Promise.all([endpoints.locations(), endpoints.orgUnits()]);
      return { locations: locations.items, units: units.items };
    },
    [],
  );

  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [description, setDescription] = useState('');
  const [deactivating, setDeactivating] = useState<LocationRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function tambah() {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.createLocation({
        code: code.trim(),
        name: name.trim(),
        org_unit_id: unit,
        description: description.trim() === '' ? null : description.trim(),
      });
      setToast(t('locations.added', { code: code.trim() }));
      setAdding(false);
      setCode('');
      setName('');
      setDescription('');
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function nonaktifkan(row: LocationRow) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.deactivateLocation(row.id);
      setToast(t('locations.deactivated', { code: row.code }));
      setDeactivating(null);
      await data.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen
      title={t('locations.title')}
      subtitle={t('locations.subtitle')}
      resource={data}
      actions={
        <Button variant="primary" onClick={() => setAdding(true)}>
          {t('locations.add')}
        </Button>
      }
    >
      {(loaded) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          {loaded.locations.length === 0 ? (
            <EmptyState
              title={t('locations.empty.title')}
              body={t('locations.empty.body')}
              action={{ label: t('locations.add'), onClick: () => setAdding(true) }}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('locations.column.code')}</th>
                  <th scope="col">{t('locations.column.name')}</th>
                  <th scope="col">{t('locations.column.unit')}</th>
                  <th scope="col">{t('locations.column.open')}</th>
                  <th scope="col">{t('locations.column.completion')}</th>
                  <th scope="col">{t('locations.column.state')}</th>
                  <th scope="col">{t('locations.column.action')}</th>
                </tr>
              </thead>
              <tbody>
                {loaded.locations.map((row) => (
                  <tr key={row.id}>
                    <td>{row.code}</td>
                    <td>{row.name}</td>
                    <td>{row.org_unit_name ?? '—'}</td>
                    {/*
                      `undefined` dan `0` DIBEDAKAN — UAT U2-12. `open_item_count` tidak
                      ada ketika M02 tidak ter-entitlement, dan menampilkan "0" untuk
                      "tidak diketahui" adalah angka yang selalu hijau dan tidak pernah
                      dipertanyakan.
                    */}
                    <td>{row.open_item_count === undefined ? '—' : row.open_item_count}</td>
                    <td>
                      {row.completion_pct === undefined
                        ? '—'
                        : t('locations.completion.value', { pct: row.completion_pct })}
                    </td>
                    <td>
                      <Badge badgeTone={row.state === 'ACTIVE' ? 'SUCCESS' : 'NEUTRAL'}>
                        {t(`locations.state.${row.state}`)}
                      </Badge>
                    </td>
                    <td>
                      {row.state === 'ACTIVE' ? (
                        <Button variant="text" onClick={() => setDeactivating(row)}>
                          {t('locations.deactivate')}
                        </Button>
                      ) : (
                        <span className="card__muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/*
            Cetak label QR belum dapat dilakukan dari sini, dan itu dikatakan alih-alih
            disembunyikan: rutenya ada di kontrak dan handlernya belum. Sebuah tombol yang
            menjawab 501 lebih buruk daripada satu kalimat yang jujur.
          */}
          <p className="card__muted">{t('locations.qrPending')}</p>

          <Dialog
            open={adding}
            kind="confirm"
            title={t('locations.add')}
            description={t('locations.add.describe')}
            confirmLabel={t('app.save')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            confirmDisabled={code.trim() === '' || name.trim() === '' || unit === ''}
            onConfirm={() => void tambah()}
            onCancel={() => setAdding(false)}
          >
            <Input
              label={t('locations.column.code')}
              value={code}
              maxLength={40}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              hint={t('locations.add.codeHint')}
            />
            <Input
              label={t('locations.column.name')}
              value={name}
              maxLength={160}
              onChange={(e) => setName(e.target.value)}
            />
            <Select
              label={t('locations.column.unit')}
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              options={[
                { value: '', label: t('report.field.choose') },
                ...loaded.units.map((u) => ({ value: u.id, label: `${u.code} — ${u.name}` })),
              ]}
            />
            <Input
              label={t('locations.add.description')}
              value={description}
              maxLength={300}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Dialog>

          {/*
            Dialog `confirm`, BUKAN `destructive`: menonaktifkan area dapat dibatalkan, dan
            data lamanya tetap terbaca. Memakai dialog merah di sini akan mengajari orang
            bahwa merah tidak berarti apa-apa (UAT D-16, pada arah yang berlawanan).
          */}
          <Dialog
            open={deactivating !== null}
            kind="confirm"
            title={t('locations.deactivate.title')}
            description={t('locations.deactivate.describe', { code: deactivating?.code ?? '' })}
            confirmLabel={t('locations.deactivate')}
            cancelLabel={t('app.cancel')}
            busy={busy}
            onConfirm={() => {
              if (deactivating) void nonaktifkan(deactivating);
            }}
            onCancel={() => setDeactivating(null)}
          />

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind="success"
                signalWord={t('signal.success')}
                title={toast}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}
        </>
      )}
    </Screen>
  );
}
